import { divider, primary, text } from "@/theme/colors";
import { Box, Stack, Typography, css, styled } from "@mui/material";
import { FC, ReactNode, useEffect, useRef, useState } from "react";

export type TNavSection = {
  id: string;
  title: string;
  content: ReactNode;
};

type TSectionNavLayoutProps = {
  sections: TNavSection[];
  footer?: ReactNode;
  navWidth?: number;
  contentWidth?: number;
  columnGap?: number;
};

const SCROLL_OFFSET = 80;

/**
 * Two-column form layout: a sticky anchor nav that highlights the section in view, plus the stacked sections.
 * Mirrors the behaviour of `UserSettingLayout`, which is deliberately left untouched — keep the two in sync by hand.
 */
export const SectionNavLayout: FC<TSectionNavLayoutProps> = ({ sections, footer, navWidth = 258, contentWidth = 852, columnGap = 24 }) => {
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const refs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: `-${SCROLL_OFFSET}px 0px -55% 0px`, threshold: 0 },
    );
    sections.forEach((section) => {
      const el = refs.current[section.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  // Keep the highlight on a section that still exists when the section list changes (e.g. switching tabs).
  useEffect(() => {
    if (!sections.some((section) => section.id === activeId)) setActiveId(sections[0]?.id);
  }, [sections, activeId]);

  const handleClickNav = (id: string) => {
    refs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveId(id);
  };

  return (
    <Row gap={`${columnGap}px`}>
      <Nav width={`${navWidth}px`}>
        {sections.map((section) => (
          <NavItem key={section.id} active={activeId === section.id} onClick={() => handleClickNav(section.id)}>
            <Typography variant="body14" color={text.primary}>
              {section.title}
            </Typography>
          </NavItem>
        ))}
      </Nav>

      <Content maxWidth={`${contentWidth}px`} gap={`${columnGap}px`}>
        {sections.map((section) => (
          <Box
            key={section.id}
            id={section.id}
            ref={(el: HTMLDivElement | null) => (refs.current[section.id] = el)}
            sx={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}
          >
            {section.content}
          </Box>
        ))}
        {footer}
      </Content>
    </Row>
  );
};

const Row = styled(Stack)`
  flex-direction: row;
  align-items: flex-start;
`;

const Nav = styled(Stack)`
  flex: none;
  gap: 8px;
  position: sticky;
  top: 24px;
  height: fit-content;
`;

const NavItem = styled(Box, { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>`
  border-left: 4px solid ${divider.middle};
  padding: 8px 16px 8px 12px;
  cursor: pointer;
  ${({ active }) =>
    active &&
    css`
      border-color: ${primary.main};
      pointer-events: none;
    `}
`;

const Content = styled(Stack)`
  flex: 1;
  min-width: 0;
`;
